import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, DownloadSimple, Key, MagnifyingGlass, ShieldCheck, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api, formatErr } from "@/lib/api";
import { langLabel } from "@/lib/languages";

export default function AdminPage() {
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [pwModalUser, setPwModalUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  const selectUser = async (u) => {
    setSelected(u);
    setProjectsLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}/projects`);
      setUserProjects(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
      setUserProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const toggleRole = async (u) => {
    const next = u.role === "admin" ? "editor" : "admin";
    if (!window.confirm(`Change ${u.email} role to ${next.toUpperCase()}?`)) return;
    try {
      const { data } = await api.patch(`/admin/users/${u.user_id}`, { role: next });
      setUsers((arr) => arr.map((x) => (x.user_id === u.user_id ? { ...x, role: data.role } : x)));
      if (selected?.user_id === u.user_id) setSelected({ ...selected, role: data.role });
      toast.success(`Role updated to ${data.role}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const [importing, setImporting] = useState(false);
  const importKeys = async (force = false) => {
    if (!window.confirm(
      force
        ? "FORCE: this will OVERWRITE your current admin STT keys with any keys found on other user accounts. Continue?"
        : "Scan all user accounts and merge any STT keys they have into the admin (platform-wide). Blank admin fields fill first; conflicting values are skipped."
    )) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/admin/import-stt-keys${force ? "?force=true" : ""}`);
      if (!data.imported_from?.length) {
        toast.success("Nothing to import — every STT key is already on the admin account.");
      } else {
        toast.success(`Imported ${data.imported_keys.length} key(s) from ${data.imported_from.length} user(s)`);
        if (data.skipped_keys?.length) {
          toast.message(`Skipped (already set on admin): ${data.skipped_keys.join(", ")}`);
        }
      }
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 mb-10">
        <div>
          <div className="overline text-[--muted] mb-3 inline-flex items-center gap-2">
            <ShieldCheck weight="bold" size={14} /> ADMIN CONSOLE
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Users & access</h1>
          <p className="mt-3 text-[--muted] max-w-xl">
            Manage roles, reset passwords, and inspect any user's projects.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => importKeys(false)}
              disabled={importing}
              data-testid="import-stt-keys-btn"
              className="border border-[--ink] px-3 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--ink] hover:text-white transition-colors disabled:opacity-50"
              title="Pull STT keys saved on user accounts into the admin (platform-wide). Blank admin fields filled first."
            >
              <DownloadSimple weight="bold" size={12} />
              {importing ? "Importing…" : "Import STT keys from users"}
            </button>
            <button
              onClick={() => importKeys(true)}
              disabled={importing}
              data-testid="import-stt-keys-force-btn"
              className="border border-[--ink] px-3 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--accent] hover:text-white hover:border-[--accent] transition-colors disabled:opacity-50"
              title="DANGER: overwrite admin's current keys with whatever values are found on user accounts"
            >
              <DownloadSimple weight="bold" size={12} /> Force overwrite
            </button>
          </div>
        </div>
        <div className="relative w-full sm:w-80">
          <MagnifyingGlass weight="bold" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[--muted]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name…"
            data-testid="admin-search-input"
            className="w-full border border-[--ink] pl-9 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-px bg-[--line] border border-[--line]">
        <div className="lg:col-span-2 bg-white">
          {loading ? (
            <div className="p-6 overline text-[--muted]">Loading users…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 overline text-[--muted]">No users match.</div>
          ) : (
            <div className="divide-y divide-[--line]">
              {filtered.map((u) => (
                <div
                  key={u.user_id}
                  className={`p-5 flex items-center gap-4 hover:bg-[--paper] transition-colors ${selected?.user_id === u.user_id ? "bg-[--paper]" : ""}`}
                  data-testid="admin-user-row"
                >
                  <div className="w-10 h-10 grid place-items-center bg-[--ink] text-white font-mono text-sm shrink-0">
                    {(u.name || u.email || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm truncate">{u.name || u.email}</span>
                      {u.role === "admin" && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[--ink] text-white">ADMIN</span>
                      )}
                      <span className="text-[10px] font-mono text-[--muted]">{u.provider || "email"}</span>
                    </div>
                    <div className="text-xs font-mono text-[--muted] truncate">{u.email}</div>
                    <div className="text-xs font-mono text-[--muted] mt-0.5">{u.projects_count || 0} project{u.projects_count === 1 ? "" : "s"}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    <button
                      onClick={() => selectUser(u)}
                      data-testid="admin-view-projects-btn"
                      className="border border-[--ink] px-3 py-2 text-xs font-semibold hover:bg-[--ink] hover:text-white transition-colors inline-flex items-center gap-1"
                    >
                      <User weight="bold" size={12} /> Projects
                    </button>
                    <button
                      onClick={() => toggleRole(u)}
                      data-testid="admin-toggle-role-btn"
                      className="border border-[--ink] px-3 py-2 text-xs font-semibold hover:bg-[--ink] hover:text-white transition-colors"
                    >
                      {u.role === "admin" ? "Demote" : "Promote"}
                    </button>
                    {u.provider !== "google" && (
                      <button
                        onClick={() => setPwModalUser(u)}
                        data-testid="admin-reset-password-btn"
                        className="border border-[--ink] px-3 py-2 text-xs font-semibold hover:bg-[--ink] hover:text-white transition-colors inline-flex items-center gap-1"
                      >
                        <Key weight="bold" size={12} /> Password
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white p-6">
          {!selected ? (
            <div className="text-sm text-[--muted]">Select a user to inspect their projects.</div>
          ) : (
            <div>
              <div className="overline text-[--muted] mb-2">USER PROJECTS</div>
              <div className="font-display font-black text-2xl tracking-tighter mb-1">{selected.name || selected.email}</div>
              <div className="text-xs font-mono text-[--muted] mb-5">{selected.email}</div>
              {projectsLoading ? (
                <div className="overline text-[--muted]">Loading…</div>
              ) : userProjects.length === 0 ? (
                <div className="text-sm text-[--muted]">No projects.</div>
              ) : (
                <ul className="space-y-3">
                  {userProjects.map((p) => (
                    <li key={p.project_id} className="border border-[--line] p-3 hover:bg-[--paper] transition-colors">
                      <button
                        className="w-full text-left"
                        onClick={() => nav(`/project/${p.project_id}`)}
                        data-testid="admin-open-project-btn"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-sm truncate">{p.title}</span>
                          <ArrowRight weight="bold" size={12} />
                        </div>
                        <div className="font-mono text-[11px] text-[--muted] mt-1">
                          {langLabel(p.source_language)} · {p.status?.toUpperCase()} · {p.translations_count} translations
                        </div>
                        {p.tags?.length > 0 && (
                          <div className="mt-1 flex gap-1 flex-wrap">
                            {p.tags.map((t) => (
                              <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 bg-[--paper] border border-[--line]">{t}</span>
                            ))}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {pwModalUser && (
        <PasswordResetModal
          user={pwModalUser}
          onClose={() => setPwModalUser(null)}
          onDone={() => setPwModalUser(null)}
        />
      )}
    </AppShell>
  );
}

function PasswordResetModal({ user, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.user_id}`, { password: pw });
      toast.success(`Password updated for ${user.email}`);
      onDone();
    } catch (e2) {
      toast.error(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white hard-border max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-password-modal">
        <div className="overline text-[--muted] mb-2">RESET PASSWORD</div>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-1">{user.name || user.email}</h2>
        <p className="text-xs font-mono text-[--muted] mb-5">{user.email}</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="overline block mb-2">New password</label>
            <input
              type="text"
              autoFocus
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="admin-new-password-input"
              className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
              placeholder="At least 6 characters"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-3 text-sm font-semibold hover:text-[--accent]">Cancel</button>
            <button
              type="submit"
              disabled={busy || pw.length < 6}
              data-testid="admin-password-submit"
              className="bg-[--accent] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50"
            >
              {busy ? "Saving…" : "Update password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
